import {
  Alert,
  Box,
  Button,
  Divider,
  FormControl,
  Grid,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
  useTheme,
  useMediaQuery,
  Dialog,
  DialogActions,
} from "@mui/material";
import Tooltip from "@mui/material/Tooltip";
import {
  DatePicker,
  LocalizationProvider,
  PickersDay,
  PickersDayProps,
} from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";
import React, { useEffect, useMemo, useState, useCallback } from "react";
import DateHelper from "@/app/helpers/DateHelper";
import AccordionSection from "../../shared/accordionSection";
import { DeliveryTimeOptionEnum } from "@/enums/deliveryTimeOptionEnum";
import { DELIVERY_TIME_OPTIONS } from "@/app/options/DeliveryTimeOptions";
import { useMutation } from "@tanstack/react-query";
import dataService from "@/app/services/dataService";
import { NotificationTypeEnum } from "@/enums/notificationTypeEnum";
import { ERROR_MESSAGE } from "@/app/constants/constants";
import { useGlobalNotification } from "../../header/GlobalNotificationContext";
import NeedsDeliveryScheduledProps from "@/app/interfaces/props/NeedsDeliveryScheduledProps";
import PageLoadingOverlay from "../../shared/pageLoadingOverlay";

const DEADLINE_HOUR = 14;
const ACCORDION_TITLE = "Schedule Delivery";

type DayWithReasonProps = Omit<PickersDayProps<Dayjs>, "key"> & {
  reason: string | null;
};

const NeedsDeliveryScheduled = ({
  orderId,
  holidays,
  scheduledDate,
  deliveryTime,
  needsDeliveryScheduled,
}: NeedsDeliveryScheduledProps) => {
  const { setNotification } = useGlobalNotification();

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const now = dayjs();
  const [isSuccessfullyScheduled, setIsSuccessfullyScheduled] =
    useState<boolean>(false);
  const isScheduled =
    (!!scheduledDate && !needsDeliveryScheduled) || isSuccessfullyScheduled;
  const [date, setDate] = useState<Dayjs | null>(null);
  const [note, setNote] = useState<string>("");
  const [time, setTime] = useState<DeliveryTimeOptionEnum>(
    DeliveryTimeOptionEnum.MORNING
  );

  const [isDatePickerOpen, setIsDatePickerOpen] = useState<boolean>(false);

  // ERROR POPUP
  const [errorDialogOpen, setErrorDialogOpen] = useState(false);
  const [errorDialogMessage, setErrorDialogMessage] = useState("");
  const handleErrorDialogClose = () => {
    setErrorDialogOpen(false);
    setErrorDialogMessage("");
  };

  const holidaySet = useMemo(
    () =>
      new Set(holidays.map((holiday) => dayjs(holiday).format("YYYY-MM-DD"))),
    [holidays]
  );

  const DayWithReason = (props: DayWithReasonProps) => {
    const { reason, ...dayProps } = props;
    const dayNode = (
      <PickersDay
        {...dayProps}
        disabled={Boolean(reason) || dayProps.disabled}
      />
    );

    if (!reason) {
      return dayNode;
    }

    return (
      <Tooltip title={reason} placement="top" arrow>
        <span>{dayNode}</span>
      </Tooltip>
    );
  };

  const nextBusinessDay = (from: Dayjs) => {
    let date = from.add(1, "day").startOf("day");

    while (DateHelper.isWeekend(date)) {
      date = date.add(1, "day");
    }

    return date;
  };

  const reasonDateIsBlocked = useCallback(
    (date: Dayjs, deadlineHour: number): string | null => {
      if (date.isBefore(now.startOf("day"))) {
        return "You cannot select a day in the past.";
      }

      if (DateHelper.isToday(date)) {
        return "You cannot select the present day.";
      }

      if (DateHelper.isWeekend(date)) {
        return "You cannot select a weekend day.";
      }

      if (holidaySet.has(date.format("YYYY-MM-DD"))) {
        return "You cannot select a public holiday.";
      }

      if (now.hour() >= deadlineHour) {
        const nbd = nextBusinessDay(now);

        if (date.isSame(nbd, "day")) {
          return "Deadline for next business day delivery has passed.";
        }
      }

      return null;
    },
    [now, holidaySet]
  );

  const shouldDisableDate = useCallback(
    (date: Dayjs) => Boolean(reasonDateIsBlocked(date, DEADLINE_HOUR)),
    [reasonDateIsBlocked]
  );

  const minSelectableDate = useMemo(
    () => now.add(1, "day").startOf("day"),
    [now]
  );

  const firstAvailableDate = useMemo(() => {
    let date = minSelectableDate;
    for (let i = 0; i < 120; i++) {
      if (!shouldDisableDate(date)) {
        return date;
      }

      date = date.add(1, "day");
    }

    return null;
  }, [minSelectableDate, shouldDisableDate]);

  useEffect(() => {
    if (isScheduled && scheduledDate) {
      setDate(dayjs(scheduledDate).startOf("day"));
    } else if (date == null && firstAvailableDate) {
      setDate(firstAvailableDate);
    }
  }, [date, firstAvailableDate, isScheduled, scheduledDate]);

  const renderDay = (
    date: Dayjs,
    _selected: (Dayjs | null)[],
    pickersDayProps: PickersDayProps<Dayjs>
  ) => {
    const reason = reasonDateIsBlocked(date, DEADLINE_HOUR);

    const { key, ...restDayProps } = pickersDayProps as unknown as {
      key: React.Key;
    } & PickersDayProps<Dayjs>;

    return (
      <DayWithReason key={key} {...restDayProps} reason={reason} day={date} />
    );
  };

  const handleDatePickerOpen = useCallback(() => {
    if (!isScheduled) {
      setIsDatePickerOpen(true);
    }
  }, [isScheduled]);

  const handleDatePickerClose = useCallback(() => {
    setIsDatePickerOpen(false);
  }, []);

  const handleDateChange = (newValue: Dayjs | null) => {
    setIsDatePickerOpen(false);

    if (!newValue) {
      setDate(null);

      return;
    }

    const normalized = newValue.startOf("day");

    if (
      !normalized.isValid() ||
      shouldDisableDate(normalized) ||
      normalized.isBefore(minSelectableDate)
    ) {
      setDate(firstAvailableDate ?? null);

      return;
    }
    setDate(normalized);
  };

  const handleSave = useMutation({
    mutationFn: () =>
      dataService.orderDeliveryScheduleById(
        {
          note,
          time,
          date: date?.format("MM/DD/YYYY") ?? "",
        },
        orderId
      ),

    onSuccess: (res) => {
      if (res.success) {
        setNotification(res.message, NotificationTypeEnum.SUCCESS);
        setIsSuccessfullyScheduled(true);
      } else {
        setErrorDialogMessage(res.error || ERROR_MESSAGE);
        setErrorDialogOpen(true);
        setIsSuccessfullyScheduled(false);
      }
    },
    onError: (err: any) => {
      setErrorDialogMessage(err?.error ?? err?.message ?? ERROR_MESSAGE);
      setErrorDialogOpen(true);
    },
  });

  const deliveryContent = (
    <Paper elevation={1} sx={{ p: 2, borderRadius: 2 }}>
      {isScheduled ? (
        <>
          <Alert severity="success">
            <Typography fontWeight="bold">DELIVERY SCHEDULED</Typography>
          </Alert>
          <Typography
            sx={{ fontStyle: "italic" }}
            color="error"
            fontSize={13}
            fontWeight="bold"
          >
            Your delivery has been scheduled. To make changes, please contact
            your branch.
          </Typography>
          <Typography variant="subtitle2" fontSize={13}>
            Selected Delivery Date: {date ? date.format("MM/DD/YYYY") : "N/A"}
          </Typography>
          <Typography variant="subtitle2" fontSize={13} sx={{ mb: 2 }}>
            Selected Delivery Time: {deliveryTime}
          </Typography>
        </>
      ) : (
        <>
          <Alert severity="warning">
            <Typography fontWeight="bold">
              NEEDS DELIVERY SCHEDULED – PLEASE SCHEDULE
            </Typography>
          </Alert>
          <Typography sx={{ mb: 1, fontStyle: "italic" }}>
            A delivery date and time is not scheduled for this delivery. Please
            select below.
          </Typography>
        </>
      )}
      <Grid container spacing={2} alignItems="flex-start">
        <Grid item xs={12} md={6}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
            Delivery Date:
          </Typography>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker<Dayjs>
              value={date}
              onChange={handleDateChange}
              open={isDatePickerOpen}
              onOpen={handleDatePickerOpen}
              onClose={handleDatePickerClose}
              renderInput={(params) => (
                <TextField
                  {...params}
                  fullWidth
                  size="small"
                  onClick={handleDatePickerOpen}
                />
              )}
              shouldDisableDate={shouldDisableDate}
              minDate={minSelectableDate}
              renderDay={renderDay}
              disabled={isScheduled}
            />
          </LocalizationProvider>
        </Grid>
        <Grid item xs={12} md={6}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
            Delivery Time:
          </Typography>
          <FormControl fullWidth size="small">
            <Select
              value={time}
              onChange={(e) =>
                setTime(e.target.value as DeliveryTimeOptionEnum)
              }
              disabled={isScheduled}
            >
              {DELIVERY_TIME_OPTIONS.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12}>
          <TextField
            label="Notes"
            placeholder="Optional notes about delivery…"
            fullWidth
            multiline
            value={note}
            onChange={(e) => setNote(e.target.value)}
            disabled={isScheduled}
          />
        </Grid>
        <Grid item xs={12}>
          <Divider sx={{ my: 1 }} />
          <Box
            display="flex"
            justifyContent={{ xs: "stretch", sm: "flex-end" }}
          >
            <Button
              variant="contained"
              onClick={() => handleSave.mutate()}
              disabled={!date || !time || isScheduled}
            >
              Save
            </Button>
          </Box>
        </Grid>
      </Grid>
      {handleSave.isPending && <PageLoadingOverlay text="Scheduling..." />}
    </Paper>
  );

  return (
    <>
      {/* ERROR POPUP */}
      <Dialog open={errorDialogOpen} onClose={handleErrorDialogClose}>
        <Typography style={{ padding: "50px" }}>
          {errorDialogMessage}
        </Typography>
        <DialogActions sx={{ justifyContent: "center", paddingBottom: "50px" }}>
          <Button
            onClick={handleErrorDialogClose}
            autoFocus
            sx={{
              padding: "12px 40px",
              fontSize: "1.1rem",
            }}
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>

      {isMobile ? (
        <AccordionSection title={ACCORDION_TITLE}>
          {deliveryContent}
        </AccordionSection>
      ) : (
        deliveryContent
      )}
    </>
  );
};

export default NeedsDeliveryScheduled;
